import * as React from "react"
import { XIcon } from "lucide-react"
import { Dialog as DialogPrimitive } from "radix-ui"

import { cn } from "../../../lib/utils"
import { useOverlayLock, guardWhenLocked, OVERLAY_LOCKED_CLASSES } from "../../../providers/overlay-lock-provider"

function Dialog(props: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger(props: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal(props: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose(props: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

/**
 * Dialog — DESIGN.md `modal-*`:
 * - `modal-confirmation`: 480px width, 8px radius, single shadow.
 * - `modal-form`: 720px width, 8px radius, gray-50 footer bg.
 * - Scrim: rgba(0, 0, 0, 0.4).
 *
 * The default `DialogContent` is the confirmation surface; pass
 * `variant="form"` for the form modal.
 */
function DialogOverlay({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "fixed inset-0 z-(--z-modal) bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
        className
      )}
      {...props}
    />
  )
}

interface DialogContentProps extends React.ComponentProps<typeof DialogPrimitive.Content> {
  /** "confirmation" = 480px (default), "form" = 720px */
  variant?: "confirmation" | "form"
}

function DialogContent({
  className,
  children,
  variant = "confirmation",
  inert,
  "aria-busy": ariaBusy,
  onEscapeKeyDown,
  onInteractOutside,
  ...props
}: DialogContentProps) {
  const isLocked = useOverlayLock()
  const widthClass = variant === "form" ? "sm:max-w-[720px]" : "sm:max-w-[480px]"
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        data-variant={variant}
        {...props}
        // Lock attrs after `{...props}` so consumers cannot re-enable a locked overlay.
        inert={isLocked || inert}
        aria-busy={isLocked || ariaBusy}
        onEscapeKeyDown={guardWhenLocked(isLocked, onEscapeKeyDown)}
        onInteractOutside={guardWhenLocked(isLocked, onInteractOutside)}
        className={cn(
          "fixed top-1/2 left-1/2 z-(--z-modal) grid w-full max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-md border border-border bg-background shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out",
          widthClass,
          variant === "form" ? "p-5" : "p-6",
          className,
          isLocked && OVERLAY_LOCKED_CLASSES,
        )}
      >
        {children}
        <DialogPrimitive.Close className="absolute top-4 right-4 inline-flex size-7 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40">
          <XIcon className="size-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="dialog-header" className={cn("flex flex-col gap-1.5", className)} {...props} />
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      // DESIGN.md `modal-form` footer: gray-50 background, right-aligned buttons
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        "[[data-variant=form]_&]:-mx-5 [[data-variant=form]_&]:-mb-5 [[data-variant=form]_&]:mt-0 [[data-variant=form]_&]:rounded-b-md [[data-variant=form]_&]:border-t [[data-variant=form]_&]:border-border [[data-variant=form]_&]:bg-background [[data-variant=form]_&]:px-5 [[data-variant=form]_&]:py-3",
        className
      )}
      {...props}
    />
  )
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return <DialogPrimitive.Title data-slot="dialog-title" className={cn("text-[15px] leading-tight font-normal text-foreground", className)} {...props} />
}

function DialogDescription({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return <DialogPrimitive.Description data-slot="dialog-description" className={cn("text-xs text-muted-foreground", className)} {...props} />
}

export {
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogClose,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription
}
